import * as React from "react";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { CapabilityCard } from "@/api/capabilities";

/** One catalogue answer. ``onNavigate`` runs when Show me is followed
 *  (the panel closes so the page it opens is visible). */
export function CapabilityCardView(props: { card: CapabilityCard; onNavigate: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const { card } = props;
  const [expanded, setExpanded] = React.useState(false);
  const steps = expanded ? card.how : card.how.slice(0, 1);
  const hidden = card.how.length - steps.length;
  return (
    <Box component="article" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" component="h3" sx={{ flex: 1, minWidth: 0 }}>
          {card.title}
        </Typography>
        {card.kind === "limitation" && <Chip size="small" color="warning" label={t("ask.partly")} />}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {card.summary}
      </Typography>
      {steps.length > 0 && (
        <Box component="ol" sx={{ pl: 2.5, my: 1 }}>
          {steps.map((step) => (
            <li key={step}>
              <Typography variant="body2">{step}</Typography>
            </li>
          ))}
        </Box>
      )}
      {hidden > 0 && (
        <Button size="small" onClick={() => setExpanded(true)}>
          {t("ask.more_steps", { count: hidden })}
        </Button>
      )}
      {card.roadmap?.status && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t("ask.fw_label", { fw: card.roadmap.fw })}
          {card.roadmap.title ? ` · ${card.roadmap.title} · ` : " · "}
          <span>{t(`ask.status.${card.roadmap.status}`)}</span>
        </Typography>
      )}
      {card.link && (
        <Link
          component={RouterLink}
          to={card.link}
          onClick={props.onNavigate}
          variant="body2"
          sx={{ display: "block", mt: 1 }}
        >
          {t("ask.show_me")}
        </Link>
      )}
    </Box>
  );
}
